import { useEffect, useState } from 'react'
import { SyncDesktopIcon } from '@/components/icons'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { ConnectionProblemAlert } from '@/components/ConnectionProblemAlert'
import { ConnectionSelect } from '@/components/ConnectionSelect'
import { connectionProblemMessage, isConnectionProblem } from '@/lib/connectionErrors'
import { WorkItemStatusBadge } from '@/components/projects/WorkItemStatusBadge'
import {
  useConnectionChoice,
  usePushWorkItemsToDevOps,
  useWorkItemAzureDevOpsProjects,
  useWorkItemAzureDevOpsEpics,
  useWorkItemAzureDevOpsFeatures,
} from '@/hooks'
import type { WorkItem } from '@/types'

interface PushToDevOpsDialogProps {
  projectId: string
  workItems: WorkItem[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onPushed?: () => void
}

const NONE_VALUE = 'none'

function formatNumber(num: number | null): string {
  if (num === null) return '—'
  return `WI-${String(num).padStart(3, '0')}`
}

export function PushToDevOpsDialog({ projectId, workItems, open, onOpenChange, onPushed }: PushToDevOpsDialogProps) {
  const pushMutation = usePushWorkItemsToDevOps(projectId)
  const connection = useConnectionChoice(projectId, 'AzureDevOps')
  const queriesEnabled = open && !connection.isLoading && !connection.isChoiceMissing
  const {
    data: projects = [],
    isLoading: projectsLoading,
    isError: projectsFailed,
    error: projectsError,
  } = useWorkItemAzureDevOpsProjects(projectId, queriesEnabled, connection.connectorId)

  const [destinationProject, setDestinationProject] = useState<string>('')
  const [selectedEpicId, setSelectedEpicId] = useState<string>('')
  const [selectedFeatureId, setSelectedFeatureId] = useState<string>('')
  const [submitError, setSubmitError] = useState<unknown>(null)

  const epicIdNumber = selectedEpicId ? Number(selectedEpicId) : null

  const {
    data: epics = [],
    isError: epicsFailed,
    error: epicsError,
  } = useWorkItemAzureDevOpsEpics(projectId, destinationProject, queriesEnabled, connection.connectorId)
  const {
    data: features = [],
    isError: featuresFailed,
    error: featuresError,
  } = useWorkItemAzureDevOpsFeatures(projectId, destinationProject, epicIdNumber, queriesEnabled, connection.connectorId)

  const resetConnection = connection.reset

  useEffect(() => {
    if (!open) {
      setDestinationProject('')
      resetConnection()
      return
    }
    if (projects.length > 0 && !projects.some(p => p.name === destinationProject)) {
      setDestinationProject(projects[0].name)
    }
  }, [open, projects, destinationProject, resetConnection])

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setSelectedEpicId('')
      setSelectedFeatureId('')
      setSubmitError(null)
    }
    onOpenChange(next)
  }

  const handleConnectionChange = (connectorId: string) => {
    connection.select(connectorId)
    setDestinationProject('')
    setSelectedEpicId('')
    setSelectedFeatureId('')
    setSubmitError(null)
  }

  const handleDestinationChange = (value: string) => {
    setDestinationProject(value)
    setSelectedEpicId('')
    setSelectedFeatureId('')
  }

  const handleEpicChange = (value: string) => {
    setSelectedEpicId(value === NONE_VALUE ? '' : value)
    setSelectedFeatureId('')
  }

  const parentWorkItemId = selectedFeatureId
    ? Number(selectedFeatureId)
    : epicIdNumber ?? undefined

  const handlePush = async () => {
    if (workItems.length === 0 || !destinationProject || connection.isChoiceMissing) return
    setSubmitError(null)
    try {
      const result = await pushMutation.mutateAsync({
        workItemIds: workItems.map(w => w.id),
        destinationProject,
        parentWorkItemId,
        connectorId: connection.connectorId,
      })
      const total = result.pushed.length + result.failed.length
      if (result.failed.length === 0) {
        toast.success(`Pushed ${result.pushed.length} item${result.pushed.length === 1 ? '' : 's'} to Azure DevOps`)
      } else if (result.pushed.length === 0) {
        toast.error(result.failed[0]?.errorMessage ?? `Failed to push ${result.failed.length} item${result.failed.length === 1 ? '' : 's'}`)
      } else {
        toast.warning(`Pushed ${result.pushed.length} of ${total}. ${result.failed.length} failed.`)
      }
      handleOpenChange(false)
      onPushed?.()
    } catch (error) {
      toast.error('Failed to push to Azure DevOps', { description: connectionProblemMessage(error, connection.selectedName) })
      if (isConnectionProblem(error)) setSubmitError(error)
    }
  }

  const noProjects = queriesEnabled && !projectsLoading && !projectsFailed && projects.length === 0

  const showParentSection = !!destinationProject && epics.length > 0
  const showFeatureSelect = !!epicIdNumber && features.length > 0
  const parentListFailed = !!destinationProject && (epicsFailed || (!!epicIdNumber && featuresFailed))
  const parentListError = epicsFailed ? epicsError : featuresError

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[640px]" data-testid="push-to-devops-dialog">
        <DialogHeader>
          <DialogTitle>Push to Azure DevOps</DialogTitle>
        </DialogHeader>

        <ConnectionSelect
          id="push-to-devops-connection"
          connections={connection.connections}
          value={connection.selectedId}
          onChange={handleConnectionChange}
        />

        <div className="space-y-2">
          <label htmlFor="push-to-devops-destination" className="text-sm font-medium">
            Destination in Azure DevOps
          </label>
          <Select value={destinationProject} onValueChange={handleDestinationChange} disabled={noProjects || projectsFailed || connection.isChoiceMissing}>
            <SelectTrigger id="push-to-devops-destination" data-testid="push-to-devops-destination">
              <SelectValue placeholder={projectsLoading ? 'Loading projects…' : 'Select a project'} />
            </SelectTrigger>
            <SelectContent>
              {projects.map(p => (
                <SelectItem key={p.id} value={p.name}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {projectsFailed ? (
            <ConnectionProblemAlert projectId={projectId} error={projectsError} connectionName={connection.selectedName} connectorId={connection.usedConnectionId} />
          ) : noProjects ? (
            <p role="alert" className="text-xs text-destructive">
              No Azure DevOps projects available. Check the Azure DevOps connector.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              {destinationProject
                ? <>Work items will be created in project <span className="font-medium text-foreground">{destinationProject}</span>. A Qiva back-reference will be added to each work item&apos;s description and acceptance criteria for traceability.</>
                : 'A Qiva back-reference will be added to each work item’s description and acceptance criteria for traceability.'}
            </p>
          )}
        </div>

        {parentListFailed && (
          isConnectionProblem(parentListError) ? (
            <ConnectionProblemAlert
              projectId={projectId}
              error={parentListError}
              connectionName={connection.selectedName}
              connectorId={connection.usedConnectionId}
              testIdSuffix="parents"
            />
          ) : (
            <p role="alert" className="text-xs text-destructive" data-testid="push-to-devops-parents-error">
              {connectionProblemMessage(parentListError) ?? 'Failed to load Epics and Features from Azure DevOps.'}
            </p>
          )
        )}

        {showParentSection && (
          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="push-to-devops-epic" className="text-sm font-medium">
                Epic
              </label>
              <Select value={selectedEpicId || NONE_VALUE} onValueChange={handleEpicChange}>
                <SelectTrigger id="push-to-devops-epic" data-testid="push-to-devops-epic" aria-label="Epic">
                  <SelectValue placeholder="No parent" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_VALUE}>No parent</SelectItem>
                  {epics.map(e => (
                    <SelectItem key={e.id} value={String(e.id)}>
                      {e.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {showFeatureSelect && (
              <div className="space-y-2">
                <label htmlFor="push-to-devops-feature" className="text-sm font-medium">
                  Feature
                </label>
                <Select
                  value={selectedFeatureId || NONE_VALUE}
                  onValueChange={v => setSelectedFeatureId(v === NONE_VALUE ? '' : v)}
                >
                  <SelectTrigger id="push-to-devops-feature" data-testid="push-to-devops-feature" aria-label="Feature">
                    <SelectValue placeholder="No feature" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>No feature</SelectItem>
                    {features.map(f => (
                      <SelectItem key={f.id} value={String(f.id)}>
                        {f.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        <p className="text-sm text-muted-foreground">
          {workItems.length} {workItems.length === 1 ? 'item' : 'items'} will be pushed:
        </p>

        <div className="space-y-2 max-h-[260px] overflow-y-auto">
          {workItems.map(item => (
            <div
              key={item.id}
              className="flex items-center gap-3 rounded-lg border p-3"
              data-testid={`push-to-devops-item-${item.id}`}
            >
              <div className="w-20 shrink-0 text-sm text-muted-foreground font-medium">
                {formatNumber(item.number)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{item.title}</p>
                {item.feature && <p className="text-xs text-muted-foreground truncate">{item.feature}</p>}
              </div>
              <WorkItemStatusBadge status={item.status} />
            </div>
          ))}
        </div>

        {submitError !== null && (
          <ConnectionProblemAlert
            projectId={projectId}
            error={submitError}
            connectionName={connection.selectedName}
            connectorId={connection.usedConnectionId}
            testIdSuffix="submit"
          />
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handlePush}
            disabled={pushMutation.isPending || workItems.length === 0 || !destinationProject || connection.isChoiceMissing}
            data-testid="push-to-devops-confirm"
          >
            <SyncDesktopIcon className="h-4 w-4" />
            {pushMutation.isPending ? 'Pushing...' : 'Push to Azure DevOps'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
